import React from 'react';
import { FileText, Video, Image } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SocialIcon } from '../icons/SocialIcon';
import { ActionsMenu } from '../common/ActionsMenu';
import { MediaDisplay } from '../posts/previews/MediaDisplay';
import { PdfThumb } from '../common/PdfThumb';
import { normalizeMedia } from '../../utils/mediaUtils';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { PostStatus } from '../common/PostStatusDropdown';

// Helper function to get platforms as array from the new object format
const getPlatformsArray = (post: any): string[] => {
  if (!post.platforms) return [];
  if (Array.isArray(post.platforms)) return post.platforms.filter((p: any): p is string => typeof p === 'string');
  if (typeof post.platforms === 'object') return Object.keys(post.platforms);
  return [];
};

interface Post {
  id: string;
  source?: 'socialness' | 'external';
  hiddenByUser?: boolean;
  platformData?: Record<string, any>;
  platformResults?: Record<string, any>;
  content: string;
  format: 'regular' | 'reel' | 'text' | 'video';
  media: {
    type: 'video' | 'image';
    url: string;
    uploadedAt?: string;
    platforms?: string;
    fileSize?: number;
    status?: string;
    fileName?: string;
    mimeType?: string;
    thumbnailUrl?: string;
  } | null;
  platforms: string[];
  status: PostStatus;
  scheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
  youtubeVideoId?: string;
  youtubeVideoUrl?: string;
  brandId?: string;
  tiktokPublishId?: string;
  publish_id?: string;
}

interface SocialMediaCardProps {
  post: Post;
  accountImage?: string;
  brandLogo?: string;
  username?: string;
  brandName?: string;
  onDelete?: (postId: string) => void;
  onHide?: (postId: string) => void;
}

export const SocialMediaCard: React.FC<SocialMediaCardProps> = ({
  post,
  accountImage,
  brandLogo = 'https://ai-coach.socialness.ai/brand/favicon-proxy?domain=socialness.ai',
  brandName = 'Socialness.ai',
  onDelete,
  onHide
}) => {
  const { t } = useTranslation();
  const isExternal = post.source === 'external';
  // Walk every platform key present on the post and pick the first link.
  // External posts can come from any platform (YouTube, LinkedIn, …), the
  // permalink lives under platforms.<p>.results.postUrl|permalink.
  const externalPermalink = isExternal
    ? (() => {
        const pr: Record<string, any> = (post as any).platformResults || {};
        const pd: Record<string, any> = (post as any).platformData || {};
        const candidates = [
          ...Object.values(pr).map((r: any) => r?.postUrl || r?.permalink),
          ...Object.values(pd).map((p: any) => p?.results?.postUrl || p?.results?.permalink),
        ];
        return candidates.find((v: any) => typeof v === 'string' && v.length > 0) || null;
      })()
    : null;
  const getStatusEmoji = (status: PostStatus) => {
    switch (status) {
      case 'draft':
        return '📝';
      case 'scheduled':
        return '📆';
      case 'waiting_for_approval':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'published':
        return '✅';
      case 'rejected':
        return '❌';
      case 'error':
        return '⚠️';
      case 'ready_to_publish':
        return '🚀';
      default:
        return '';
    }
  };

  const getMediaIcon = (post: Post) => {
    const normalizedMedia = normalizeMedia(post.media);
    if (!normalizedMedia) {
      return <FileText className="w-4 h-4" />;
    }
    return normalizedMedia.type === 'video' ? 
      <Video className="w-4 h-4" /> : 
      <Image className="w-4 h-4" />;
  };

  const getMediaFormatEmoji = (post: Post) => {
    // Determine media format based on post properties
    const normalizedMedia = normalizeMedia(post.media);
    if (!normalizedMedia) {
      return '📝'; // Text only
    }
    
    if (normalizedMedia.type === 'video') {
      return '🎬'; // Video
    }
    
    if (normalizedMedia.type === 'image') {
      // Check if it's a carousel (multiple images) - we'd need to check if there are multiple media items
      // For now, assume single image
      return '🖼️'; // Image
    }
    
    return '📱'; // Default fallback
  };

  const getDate = () => {
    if (post.status === 'published' || post.status === 'waiting_for_approval' || post.status === 'processing') {
      return new Date(post.updatedAt).toLocaleDateString();
    } else if (post.status === 'scheduled' && post.scheduledFor) {
      return new Date(post.scheduledFor).toLocaleDateString();
    }
    return null;
  };

  const date = getDate();

  const handlePostModify = () => {
    // This will be captured by the parent onClick
  };
  
  const handlePostDelete = () => {
    if (onDelete) {
      onDelete(post.id);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex gap-3">
            <div className="flex items-center">
              <img 
                src={accountImage || brandLogo} 
                alt={brandName}
                className="w-14 h-14 rounded-lg object-cover border border-black"
              />
            </div>
            <div>
              <span className="text-base font-semibold block">{brandName}</span>
              {date && (
                <span className="text-xs text-gray-400 block">
                  {date}
                </span>
              )}
              {isExternal && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-full text-[10px] border bg-gray-100 text-gray-600 border-gray-200"
                  title={t('contentHub.externalPostTooltip', 'Cette publication a été faite directement sur la plateforme, sans passer par Socialness.') as string}
                >
                  <span>🔗</span>
                  <span>{t('contentHub.externalBadge', 'Publié hors Socialness')}</span>
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {getStatusEmoji(post.status)}
              </span>
              <ActionsMenu
                onModify={handlePostModify}
                onDelete={handlePostDelete}
                status={post.status}
                className="z-10"
                isExternal={isExternal}
                externalPermalink={externalPermalink}
                onHide={isExternal && onHide ? () => onHide(post.id) : undefined}
              />
            </div>
            <div className="flex gap-1">
              {getPlatformsArray(post).map((platform) => (
                <SocialIcon 
                  key={platform}
                  platform={platform as SocialPlatform}
                  size={16}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {(() => {
        const normalizedMedia = normalizeMedia(post.media);
        if (!normalizedMedia) return null;
        
        const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
        
        return (
          <div className="relative pb-[125%] bg-gray-100 overflow-visible">
            {isPdf ? (
              <PdfThumb 
                src={normalizedMedia.url} 
                className="absolute inset-0 w-full h-full" 
                frame="square" 
              />
            ) : (
              <MediaDisplay
                mediaUrl={normalizedMedia.url}
                altText="Post media"
                type={normalizedMedia.type}
                className="absolute inset-0 w-full h-full"
                objectFit="cover"
                controls={normalizedMedia.type === 'video'}
                autoPlayOnMount={false}
                muted={true}
              />
            )}
            {/* Media format emoji in bottom right corner */}
            <div className="absolute bottom-0 right-0 bg-white text-gray-800 text-xs px-2 py-1 rounded-full flex items-center gap-1 transform translate-x-1/2 translate-y-1/2 shadow-sm border border-gray-200">
              <span>{getMediaFormatEmoji(post)}</span>
            </div>
          </div>
        );
      })()}

      <div className="p-6">
        <div className="flex justify-between items-start">
          <div className="text-sm text-gray-700 whitespace-pre-wrap flex-1 pr-3">
            {post.content}
          </div>
          {/* Media format emoji for all posts */}
          {!normalizeMedia(post.media) && (
            <div className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full flex items-center gap-1 flex-shrink-0">
              <span>{getMediaFormatEmoji(post)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};